import { Option } from "@inrev/core";
import type { FieldErrors, FieldValues } from "react-hook-form";
import { getNestedError, SEVERITY_RANK, type Severity } from "./utils/formResolver";

const ERROR_SEVERITY: Severity = "error";

const hasSeverityInTree = (node: unknown, minRank: number): boolean => {
	if (Option.isNone(node)) {
		return false;
	}
	if (typeof node !== "object") {
		return false;
	}

	const record = node as Record<string, unknown>;
	if ("type" in record && "message" in record) {
		const rank = SEVERITY_RANK[record.type as Severity];
		return rank !== undefined && rank >= minRank;
	}

	for (const child of Object.values(record)) {
		if (hasSeverityInTree(child, minRank)) return true;
	}
	return false;
};

export const findWorstSeverityInTree = (node: unknown): Severity | null => {
	if (Option.isNone(node)) {
		return null;
	}
	if (typeof node !== "object") {
		return null;
	}

	const record = node as Record<string, unknown>;
	if ("type" in record && "message" in record) {
		const sev = record.type as Severity;
		return SEVERITY_RANK[sev] !== undefined ? sev : null;
	}

	let worst: Severity | null = null;
	for (const child of Object.values(record)) {
		const childSev = findWorstSeverityInTree(child);
		if (childSev === ERROR_SEVERITY) {
			return ERROR_SEVERITY;
		}
		if (childSev !== null) {
			if (worst === null || SEVERITY_RANK[childSev] > SEVERITY_RANK[worst]) {
				worst = childSev;
			}
		}
	}
	return worst;
};

export const stepHasErrors = <TFormData extends FieldValues>(
	errors: FieldErrors<TFormData>,
	fieldPaths: string[],
): boolean => {
	for (const path of fieldPaths) {
		const errorNode = getNestedError(errors as Record<string, unknown>, path);
		if (hasSeverityInTree(errorNode, SEVERITY_RANK[ERROR_SEVERITY])) {
			return true;
		}
	}
	return false;
};

export const getStepWorstSeverity = <TFormData extends FieldValues>(
	errors: FieldErrors<TFormData>,
	fieldPaths: string[],
): Severity | null => {
	let worst: Severity | null = null;
	for (const path of fieldPaths) {
		const errorNode = getNestedError(errors as Record<string, unknown>, path);
		const sev = findWorstSeverityInTree(errorNode);
		if (sev === ERROR_SEVERITY) {
			return ERROR_SEVERITY;
		}
		if (sev !== null) {
			if (worst === null || SEVERITY_RANK[sev] > SEVERITY_RANK[worst]) {
				worst = sev;
			}
		}
	}
	return worst;
};
